import { useMutation, useQuery } from "@tanstack/react-query";
import type { CreateCreativeFlagRequest, CreativeFlagDto } from "@nexus/contracts";
import { creativeFlagsQueryOptions } from "@/lib/queries";
import {
  createCreativeFlag,
  deleteCreativeFlag,
} from "../api/creative-flags.api";

// ─── Hooks ──────────────────────────────────────────────────────────

/**
 * Mutation to create a new creative flag.
 * Wraps the fn as `(x) => fn(x)` to avoid TanStack passing extra args.
 */
export function useCreateCreativeFlag() {
  return useMutation<CreativeFlagDto, Error, CreateCreativeFlagRequest>({
    mutationFn: (payload) => createCreativeFlag(payload),
    meta: { skipGlobalError: true },
  });
}

/**
 * Mutation to delete a creative flag by id.
 * Wraps the fn as `(x) => fn(x)` to avoid TanStack passing extra args.
 */
export function useDeleteCreativeFlag() {
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteCreativeFlag(id),
    meta: { skipGlobalError: true },
  });
}

/**
 * Query to list all flags for a given creative hash.
 * Only runs when `enabled` is true (e.g., dialog is open).
 */
export function useCreativeFlags(hash: string, enabled = true) {
  return useQuery(creativeFlagsQueryOptions(hash, enabled));
}
